import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { 
  MapPin, Award, Calendar, Mail, Phone, Building2, 
  CheckCircle, AlertCircle, XCircle, GraduationCap,
  MessageSquarePlus, Send, ArrowLeft, Loader2, Quote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ContactModal } from '../components/professionals/ContactModal';
import { RecommendationModal } from '../components/professionals/RecommendationModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ estatus }) => {
  if (estatus === 'Activo') {
    return (
      <span className="status-badge status-active">
        <CheckCircle className="w-3 h-3" />
        Activo
      </span>
    );
  }
  if (estatus === 'Suspendido') {
    return (
      <span className="status-badge status-suspended">
        <AlertCircle className="w-3 h-3" />
        Suspendido
      </span>
    );
  }
  return (
    <span className="status-badge status-inactive">
      <XCircle className="w-3 h-3" />
      Baja
    </span>
  );
};

const Recommendations = ({ recommendations, onOpenModal }) => {
  if (recommendations.length === 0) {
    return (
      <div className="text-center py-8">
        <MessageSquarePlus className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 mb-4">Este profesional aún no tiene recomendaciones</p>
        <Button variant="outline" onClick={onOpenModal} className="border-slate-200">
          Ser el primero en recomendar
        </Button>
      </div>
    );
  }
  
  const recElements = [];
  for (let i = 0; i < recommendations.length; i++) {
    const rec = recommendations[i];
    recElements.push(
      <div key={rec.id} className="recommendation-card bg-slate-50 rounded-lg p-4" data-testid={`rec-${rec.id}`}>
        <div className="flex items-start gap-3">
          <Quote className="w-5 h-5 text-slate-300 flex-shrink-0 mt-1" />
          <div className="flex-1">
            <p className="text-slate-700 mb-3">{rec.text}</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="font-medium text-sm text-slate-900">{rec.author_initials || rec.author_name}</span>
                <Badge variant="secondary" className="text-xs bg-slate-200">{rec.category}</Badge>
              </div>
              <span className="text-xs text-slate-500">{new Date(rec.created_at).toLocaleDateString('es-MX')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return <div className="space-y-4">{recElements}</div>;
};

const Formation = ({ formacion }) => {
  if (!formacion || formacion.length === 0) {
    return <p className="text-slate-500 text-center py-8">No hay información de formación disponible</p>;
  }
  
  const elements = [];
  for (let i = 0; i < formacion.length; i++) {
    const item = formacion[i];
    elements.push(
      <div key={i} className="flex gap-4 p-4 bg-slate-50 rounded-lg">
        <div className="w-10 h-10 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
          <GraduationCap className="w-5 h-5 text-slate-600" />
        </div>
        <div>
          <p className="font-medium text-slate-900">{item.nombre}</p>
          <p className="text-sm text-slate-600">{item.institucion}</p>
          <div className="flex items-center gap-3 mt-1">
            <Badge variant="secondary" className="text-xs bg-slate-100">{item.tipo}</Badge>
            {item.fecha && <span className="text-xs text-slate-500">{item.fecha}</span>}
            {item.horas && <span className="text-xs text-slate-500">{item.horas} horas</span>}
          </div>
        </div>
      </div>
    );
  }
  return <div className="space-y-4">{elements}</div>;
};

const formatArray = (arr) => {
  if (!arr || arr.length === 0) return 'No especificadas';
  return arr.join(' • ');
};

export const ProfilePage = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [professional, setProfessional] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isContactOpen, setIsContactOpen] = useState(searchParams.get('contactar') === 'true');
  const [isRecommendationOpen, setIsRecommendationOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const profRes = await fetch(`${API}/professionals/${id}`);
        const recsRes = await fetch(`${API}/recommendations/${id}`);
        if (profRes.ok) setProfessional(await profRes.json());
        if (recsRes.ok) setRecommendations(await recsRes.json());
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleRecommendationSuccess = (newRec) => {
    setRecommendations(prev => [newRec, ...prev]);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!professional) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-manrope font-bold text-xl text-slate-900 mb-2">Profesional no encontrado</h2>
          <Link to="/buscar"><Button variant="outline">Volver</Button></Link>
        </div>
      </div>
    );
  }

  const avatarUrl = professional.foto || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(professional.nombre)}&backgroundColor=f8fafc`;
  const isActive = professional.estatus === 'Activo';

  return (
    <div className="page-transition bg-slate-50 min-h-screen" data-testid="profile-page">
      <div className="bg-white border-b border-slate-200">
        <div className="container-cni py-4">
          <Link to="/buscar" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4 mr-2" />Volver
          </Link>
        </div>
      </div>

      <div className="bg-white border-b border-slate-200">
        <div className="container-cni py-8">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <img src={avatarUrl} alt={professional.nombre} className="w-28 h-28 md:w-36 md:h-36 rounded-xl object-cover bg-slate-100 border border-slate-200" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <StatusBadge estatus={professional.estatus} />
                <span className="rank-badge"><Award className="w-3 h-3 inline mr-1" />{professional.rango}</span>
              </div>
              <h1 className="font-manrope font-bold text-2xl md:text-3xl text-slate-900 mb-2">{professional.nombre}</h1>
              <p className="font-mono text-sm text-slate-500 mb-4">{professional.numero_registro}</p>
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-4">
                <div className="flex items-center"><MapPin className="w-4 h-4 mr-1 text-slate-400" />{professional.ciudad}, {professional.estado}</div>
                <div className="flex items-center"><Calendar className="w-4 h-4 mr-1 text-slate-400" />Desde {professional.anio_ingreso}</div>
                {professional.asociacion_nombre && <div className="flex items-center"><Building2 className="w-4 h-4 mr-1 text-slate-400" />{professional.asociacion_nombre}</div>}
              </div>
              <div className="flex gap-3">
                {isActive && <Button onClick={() => setIsContactOpen(true)} className="bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="profile-contact-btn"><Send className="w-4 h-4 mr-2" />Contactar</Button>}
                <Button variant="outline" onClick={() => setIsRecommendationOpen(true)} className="border-slate-200" data-testid="profile-recommend-btn"><MessageSquarePlus className="w-4 h-4 mr-2" />Recomendar</Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container-cni py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {professional.bio && (
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <h2 className="font-manrope font-semibold text-lg text-slate-900 mb-3">Acerca de</h2>
                <p className="text-slate-600 leading-relaxed">{professional.bio}</p>
              </div>
            )}

            <Tabs defaultValue="especialidades" className="bg-white border border-slate-200 rounded-lg">
              <TabsList className="w-full justify-start border-b border-slate-200 rounded-none bg-transparent p-0">
                <TabsTrigger value="especialidades" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900">Especialidades</TabsTrigger>
                <TabsTrigger value="formacion" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900">Formación</TabsTrigger>
                <TabsTrigger value="recomendaciones" className="rounded-none border-b-2 border-transparent data-[state=active]:border-slate-900">Recomendaciones ({recommendations.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="especialidades" className="p-6">
                <div className="space-y-6">
                  <div><h3 className="text-sm font-semibold text-slate-700 mb-3">Especialidades</h3><p className="text-slate-600">{formatArray(professional.especialidades)}</p></div>
                  <div><h3 className="text-sm font-semibold text-slate-700 mb-3">Operaciones</h3><p className="text-slate-600">{formatArray(professional.tipo_operaciones)}</p></div>
                  <div><h3 className="text-sm font-semibold text-slate-700 mb-3">Zonas</h3><p className="text-slate-600">{formatArray(professional.zonas_operacion)}</p></div>
                </div>
              </TabsContent>

              <TabsContent value="formacion" className="p-6"><Formation formacion={professional.formacion} /></TabsContent>
              <TabsContent value="recomendaciones" className="p-6"><Recommendations recommendations={recommendations} onOpenModal={() => setIsRecommendationOpen(true)} /></TabsContent>
            </Tabs>
          </div>

          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6">
              <h3 className="font-manrope font-semibold text-slate-900 mb-4">Verificación</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center"><CheckCircle className="w-4 h-4 text-green-600" /></div>
                  <div><p className="text-sm font-medium text-slate-900">Identidad Verificada</p><p className="text-xs text-slate-500">Documentos validados</p></div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center"><CheckCircle className="w-4 h-4 text-green-600" /></div>
                  <div><p className="text-sm font-medium text-slate-900">Registro CNI</p><p className="text-xs text-slate-500">{professional.numero_registro}</p></div>
                </div>
              </div>
            </div>

            {isActive && (professional.email || professional.telefono) && (
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <h3 className="font-manrope font-semibold text-slate-900 mb-4">Contacto</h3>
                <div className="space-y-3">
                  {professional.email && <div className="flex items-center gap-3 text-sm"><Mail className="w-4 h-4 text-slate-400" /><span className="text-slate-600">{professional.email}</span></div>}
                  {professional.telefono && <div className="flex items-center gap-3 text-sm"><Phone className="w-4 h-4 text-slate-400" /><span className="text-slate-600">{professional.telefono}</span></div>}
                </div>
                <Separator className="my-4" />
                <Button onClick={() => setIsContactOpen(true)} className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold"><Send className="w-4 h-4 mr-2" />Solicitar Contacto</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <ContactModal isOpen={isContactOpen} onClose={() => setIsContactOpen(false)} professional={professional} />
      <RecommendationModal isOpen={isRecommendationOpen} onClose={() => setIsRecommendationOpen(false)} professional={professional} onSuccess={handleRecommendationSuccess} />
    </div>
  );
};
